// Pure helpers behind "sync ทั้งร้านเข้า seller" on /admin/product_platform
// (mdf-bo-fn#280, spec docs/specs/2026-09-22-shopee-master-product-sync-design.md
// §6C.3 HTTP + §6C.6 FE).
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js), so every rule that can be
// decided — the POST body, the progress percentage, which statuses mean "still
// running", how a 409 hands back the job that is already in flight, and which
// i18n key explains one item's outcome — lives here where a spec can call it
// directly. ShopeeBulkSyncModal.vue only renders and sequences.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT (§6C.3). Built in parallel with this screen, so these
// bytes are the agreement, not an observation of a running server.
//
//   POST /api/platform/v2/shopee/bulk-sync
//        {platform_shop_id:string, category_id:int64, item_status:[string],
//         only_unlinked:bool, dry_run:bool}
//     202 {job_id, status:"queued"}
//     400 {code:"shop_required"}     platform_shop_id empty
//     400 {code:"category_required"} dry_run=false with category_id <= 0
//     400 {code:"bad_item_status"}   an item_status outside the six Shopee values
//     409 {code:"bulk_in_flight", job_id, status}
//                                    a queued/listing/running job already
//                                    exists for this (enterprise, shop) — NOT
//                                    an error for the UI: attach to job_id and
//                                    poll it (the same move AmazePushModal makes
//                                    on 422 push_in_flight).
//
//   GET  /api/platform/v2/shopee/bulk-sync?platform_shop_id=&limit=  (<=50, 10)
//     200 {jobs:[job]}   newest first
//   GET  /api/platform/v2/shopee/bulk-sync/:id                   -> 200 job
//   GET  /api/platform/v2/shopee/bulk-sync/:id/items?status=&page=&page_size=
//     200 {total, items:[item]}   ordered by seq, page_size <= 200 (default 50)
//   POST /api/platform/v2/shopee/bulk-sync/:id/cancel
//     200 job · 409 {code:"bulk_finished"} when the job already ended
//
//   job  = {id, platform_shop_id, category_id, item_status, only_unlinked,
//           dry_run, status, cancel_requested, total, processed, ok_count,
//           rejected_count, failed_count, skipped_count, created_count,
//           updated_count, last_error, created_at, started_at, finished_at,
//           updated_at}
//   item = {item_id, item_status, seq, status, action, product_id, reason_code,
//           detail, finished_at}
//          detail — ok: {skus:{create,update,deactivate,reactivate},
//                        warnings:[]}
//                 · rejected: {rejects:[{model_id, reason_code, message}]}
//                 · failed: {message}
// --------------------------------------------------------------------------

import { rejectReasonI18nKey } from "./shopeeToSeller";

// --------------------------------------------------------------------------
// vocabularies
// --------------------------------------------------------------------------

// SHOPEE_ITEM_STATUS_OPTIONS — every value Shopee's get_item_list accepts
// (§6C.0, last bullet). All six are OFFERED in the settings step so the
// operator can see what exists; only DEFAULT_ITEM_STATUS is ticked, because a
// BANNED / deleted listing is refused by the per-item core anyway (422
// item_status_not_allowed) and ticking it would only fill the result table with
// skips.
export const SHOPEE_ITEM_STATUS_OPTIONS = [
  "NORMAL",
  "UNLIST",
  "BANNED",
  "REVIEWING",
  "SELLER_DELETE",
  "SHOPEE_DELETE"
];

// DEFAULT_ITEM_STATUS is both the UI's initial tick set and the BE's own
// default for an empty item_status (§6C.3: `item_status` ว่าง ⇒
// ["NORMAL","UNLIST"]). Sent explicitly rather than relying on the default so
// the job row records what the operator saw on screen.
// Owner 2026-09-29: NORMAL only — UNLIST listings are hidden on Shopee and must
// be an explicit choice, never what a whole-shop sync drags in by default.
export const DEFAULT_ITEM_STATUS = ["NORMAL"];

// BULK_JOB_STATUSES — mdfpf_shopee_bulk_job.status (§6C.1).
export const BULK_JOB_STATUSES = [
  "queued",
  "listing",
  "running",
  "done",
  "failed",
  "cancelled"
];

// BULK_IN_FLIGHT_STATUSES: the job is still ours to watch. `queued` and
// `listing` have no denominator yet (total is only known once listing ends), so
// the progress bar is indeterminate for those two — see bulkJobPercent.
export const BULK_IN_FLIGHT_STATUSES = ["queued", "listing", "running"];

// BULK_FINISHED_STATUSES: terminal. Polling stops and the results step opens.
export const BULK_FINISHED_STATUSES = ["done", "failed", "cancelled"];

// BULK_ITEM_STATUSES — mdfpf_shopee_bulk_job_item.status (§6C.1), and also the
// `?status=` filter of the items endpoint. `pending` is a real, reachable state
// in a finished job: a cancelled job keeps its untouched items pending, and so
// does a job that died on 10 consecutive fetch failures (§6C.4).
export const BULK_ITEM_STATUSES = ["pending", "ok", "rejected", "failed", "skipped"];

// BULK_SKIPPED_REASON_CODES / BULK_FAILED_REASON_CODES — the closed sets of
// `reason_code` the runner writes (§6C.1 column comment, §6C.4 step 2-4).
export const BULK_SKIPPED_REASON_CODES = ["item_status_not_allowed", "already_linked"];
export const BULK_FAILED_REASON_CODES = [
  "fetch_failed",
  "cover_image_failed",
  "category_required",
  "save_failed"
];

// BULK_POLL_INTERVAL_MS — §6C.6 step ②: "poll GET :id ทุก 2 s".
export const BULK_POLL_INTERVAL_MS = 2000;

// BULK_ITEMS_PAGE_SIZE — the results table's page size. The endpoint caps
// page_size at 200; 50 is its default and enough for one screen.
export const BULK_ITEMS_PAGE_SIZE = 50;

// BULK_JOB_LIST_LIMIT — "งานล่าสุด" asks for this many past jobs (endpoint cap
// is 50).
export const BULK_JOB_LIST_LIMIT = 10;

// --------------------------------------------------------------------------
// small normalisers
// --------------------------------------------------------------------------

function stringOf(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function statusOf(value) {
  // Callers hold either a bare status string or the whole job row; both are
  // accepted so a caller cannot accidentally ask "is this object in flight?"
  // and get a silent false.
  if (value && typeof value === "object") return stringOf(value.status).toLowerCase();
  return stringOf(value).toLowerCase();
}

// --------------------------------------------------------------------------
// the POST body
// --------------------------------------------------------------------------

// bulkStartBody builds the POST /bulk-sync body.
//
//   bulkStartBody(shopId, {categoryId, itemStatus, onlyUnlinked, dryRun})
//     -> {platform_shop_id, category_id, item_status, only_unlinked, dry_run}
//
// platform_shop_id is a STRING, always. The Go handler binds it into a `string`
// field, so a JSON number answers 400 — the Phase 2 lesson that the to-seller
// body already carries (src/utils/shopeeToSeller.js#toSellerBody). It is NOT
// routed through Number(): a Shopee shop id is an opaque identifier and a
// numeric round trip is a precision risk for nothing. A missing / blank / zero
// shop yields "" so the BE answers its own `shop_required` instead of this
// screen inventing a shop 0.
//
// category_id is ALWAYS present as a number (0 when unset), unlike the
// per-item to-seller body which omits it: here the value is snapshotted onto
// the job row and every item of the run reads it from there (§6C.3, last
// bullet), so "absent" and "0" must not be two different things.
//
// item_status defaults to DEFAULT_ITEM_STATUS when nothing usable is given.
// Values are upper-cased, trimmed and de-duplicated, but an unrecognised one is
// NOT dropped: §6C.3 answers 400 `bad_item_status` for it, and silently
// swallowing it here would hide a real disagreement between this screen and the
// BE behind a run that quietly used different filters than the operator chose.
export function bulkStartBody(shopId, options) {
  const opts = options && typeof options === "object" ? options : {};

  const shop = stringOf(shopId);
  const category = Number(opts.categoryId);

  return {
    platform_shop_id: shop === "0" ? "" : shop,
    category_id: isFinite(category) && category > 0 ? Math.floor(category) : 0,
    item_status: normaliseItemStatus(opts.itemStatus),
    only_unlinked: opts.onlyUnlinked === true,
    dry_run: opts.dryRun === true
  };
}

// normaliseItemStatus is exported because the settings step needs the same
// normalisation to decide whether the operator has left the selection empty.
export function normaliseItemStatus(list) {
  if (!Array.isArray(list)) return DEFAULT_ITEM_STATUS.slice();
  const out = [];
  for (const entry of list) {
    const s = stringOf(entry).toUpperCase();
    if (s === "") continue;
    if (out.indexOf(s) !== -1) continue;
    out.push(s);
  }
  return out.length ? out : DEFAULT_ITEM_STATUS.slice();
}

// --------------------------------------------------------------------------
// progress
// --------------------------------------------------------------------------

// bulkJobPercent turns a job row into 0..100 for el-progress.
//
// Three cases, in this order:
//   1. queued / listing  -> 0. The job has not counted its work yet (`total`
//      is written when listing finishes, §6C.4), so any percentage would be a
//      lie; the modal renders an indeterminate "กำลังนับสินค้า…" instead and
//      keys that off the status, not off this number.
//   2. total > 0         -> round(processed/total*100), clamped. `processed` is
//      ok+rejected+failed+skipped (§6C.1).
//   3. finished with total 0 -> 100. A shop with no matching listing at all,
//      or a job cancelled during listing, is over: leaving such a job at 0%
//      would read as "stuck" forever.
// Anything else (running with total 0 — the instant between listing and the
// first item) is 0.
export function bulkJobPercent(job) {
  if (!job || typeof job !== "object") return 0;
  const status = statusOf(job);
  if (status === "queued" || status === "listing") return 0;

  const total = Number(job.total);
  if (isFinite(total) && total > 0) {
    const processed = Number(job.processed);
    if (!isFinite(processed) || processed <= 0) return 0;
    const pct = Math.round((processed / total) * 100);
    if (pct < 0) return 0;
    if (pct > 100) return 100;
    return pct;
  }

  if (BULK_FINISHED_STATUSES.indexOf(status) !== -1) return 100;
  return 0;
}

// isBulkInFlight: keep polling while this is true (§6C.6 step ②).
export function isBulkInFlight(status) {
  return BULK_IN_FLIGHT_STATUSES.indexOf(statusOf(status)) !== -1;
}

// isBulkFinished: terminal — stop polling, open the results, and (for a real
// run) let the page refresh its seller-links badges.
export function isBulkFinished(status) {
  return BULK_FINISHED_STATUSES.indexOf(statusOf(status)) !== -1;
}

// --------------------------------------------------------------------------
// 409 bulk_in_flight
// --------------------------------------------------------------------------

// BULK_IN_FLIGHT_CODE is the body `code` of the 409 (§6C.3).
export const BULK_IN_FLIGHT_CODE = "bulk_in_flight";

// BULK_FINISHED_CODE is the body `code` of the 409 that POST :id/cancel answers
// when the job has already ended — a race the operator lost by two seconds,
// not a failure: the modal just re-reads the job.
export const BULK_FINISHED_CODE = "bulk_finished";

// errorBody digs out the payload, tolerating a `data` envelope: some handlers
// in this API answer {data:{...}} and some answer the object bare, so a caller
// must not have to know which one this route picked.
function errorBody(error) {
  const data = error && error.response ? error.response.data : null;
  if (!data || typeof data !== "object") return null;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    // Prefer the inner object when it carries the code / job_id, else the outer.
    const inner = data.data;
    if (inner.code !== undefined || inner.job_id !== undefined || inner.Code !== undefined) {
      return inner;
    }
  }
  return data;
}

function bodyCode(data) {
  if (!data || typeof data !== "object") return "";
  return stringOf(data.code || data.Code || data.error_code || data.errorCode);
}

// bulkInFlightJobId returns the id of the job that is already running, or null.
//
// null, not 0: "there is no such job" is a different statement from "job 0",
// and the caller (`if (jobId !== null) attach(jobId)`) reads better for it.
// Both the 409 STATUS and the `bulk_in_flight` CODE must match — a 409 from a
// proxy, or any other coded 409 the route might grow, must not be mistaken for
// "attach to this job".
export function bulkInFlightJobId(error) {
  const status = error && error.response ? Number(error.response.status) : 0;
  if (status !== 409) return null;
  const data = errorBody(error);
  if (bodyCode(data) !== BULK_IN_FLIGHT_CODE) return null;
  const raw = data.job_id !== undefined ? data.job_id : data.JobID;
  const id = Number(raw);
  return isFinite(id) && id > 0 ? id : null;
}

// isBulkFinishedError: the cancel button's 409.
export function isBulkFinishedError(error) {
  const status = error && error.response ? Number(error.response.status) : 0;
  if (status !== 409) return false;
  return bodyCode(errorBody(error)) === BULK_FINISHED_CODE;
}

// bulkErrorI18nKey maps a start/cancel failure to the sentence the modal shows.
// `bulk_in_flight` has no key here on purpose: it is handled by attaching to the
// running job, never by showing an error.
export function bulkErrorI18nKey(error) {
  const status = error && error.response ? Number(error.response.status) : 0;
  const code = bodyCode(errorBody(error));
  if (status === 400) {
    if (code === "shop_required") return "shopee_bulk_sync.error.shop_required";
    if (code === "category_required") return "shopee_bulk_sync.error.category_required";
    if (code === "bad_item_status") return "shopee_bulk_sync.error.bad_item_status";
    return "shopee_bulk_sync.error.bad_request";
  }
  if (status === 409 && code === BULK_FINISHED_CODE) {
    return "shopee_bulk_sync.error.already_finished";
  }
  if (status === 404) return "shopee_bulk_sync.error.not_found";
  return "shopee_bulk_sync.error.generic";
}

// bulkErrorDetail is the backend's own text under the translated sentence.
export function bulkErrorDetail(error) {
  const data = errorBody(error);
  if (!data || typeof data !== "object") return "";
  return stringOf(data.Msg || data.msg || data.message || data.Message);
}

// --------------------------------------------------------------------------
// per-item outcome
// --------------------------------------------------------------------------

// bulkItemRejects pulls the 422-shaped reject list out of an item's `detail`
// (§6C.4: rejected ⇒ detail={rejects}). Shaped exactly like the per-item
// modal's 422 body, so rejectDisplayText/rejectDetailText from
// @/utils/shopeeToSeller render these rows unchanged.
export function bulkItemRejects(item) {
  if (!item || typeof item !== "object") return [];
  const detail = item.detail || item.Detail;
  if (!detail || typeof detail !== "object") return [];
  const list = detail.rejects || detail.Rejects;
  return Array.isArray(list) ? list.filter((r) => r && typeof r === "object") : [];
}

// bulkItemMessage is the BE's own sentence for a failed item (detail={message}).
export function bulkItemMessage(item) {
  if (!item || typeof item !== "object") return "";
  const detail = item.detail || item.Detail;
  if (!detail || typeof detail !== "object") return "";
  return stringOf(detail.message || detail.Message);
}

// bulkItemReasonI18nKey: the "เหตุผล" column's translation key for one item.
//
//   skipped  -> shopee_bulk_sync.reason.<reason_code>   (item_status_not_allowed
//               | already_linked)
//   failed   -> shopee_bulk_sync.reason.<reason_code>   (fetch_failed |
//               cover_image_failed | category_required | save_failed)
//   rejected -> the PER-ITEM screen's own sentence for the first reject, via
//               rejectReasonI18nKey: a bulk run rejects an item for exactly the
//               reasons one click does (model_sku_empty, no_price, …), and
//               translating them twice would let the two screens drift apart.
//               Its own fallback (shopee_to_seller.reject.unknown) covers a
//               reject code this build does not know, and a `rejected` row with
//               no reject list at all.
//   anything else (ok, pending, an unknown status, a reason_code outside the two
//               closed sets) -> shopee_bulk_sync.reason.unknown
//
// The modal only prints this for skipped/failed/rejected rows; `ok` shows the
// action tag and `pending` shows a dash. The fallback still resolves to a real
// sentence so a future BE reason_code can never render a blank cell or a raw
// identifier.
export function bulkItemReasonI18nKey(item) {
  if (!item || typeof item !== "object") return "shopee_bulk_sync.reason.unknown";
  const status = stringOf(item.status).toLowerCase();
  const code = stringOf(item.reason_code || item.ReasonCode);

  if (status === "rejected") {
    const rejects = bulkItemRejects(item);
    const first = rejects.length ? rejects[0] : null;
    const rejectCode = first ? stringOf(first.reason_code || first.ReasonCode) : "";
    return rejectReasonI18nKey(rejectCode);
  }
  if (status === "skipped" && BULK_SKIPPED_REASON_CODES.indexOf(code) !== -1) {
    return "shopee_bulk_sync.reason." + code;
  }
  if (status === "failed" && BULK_FAILED_REASON_CODES.indexOf(code) !== -1) {
    return "shopee_bulk_sync.reason." + code;
  }
  return "shopee_bulk_sync.reason.unknown";
}

// BULK_ITEM_STATUS_TAG_TYPES — el-tag `type` per item status. Element UI
// 2.4.11 ships success / info / warning / danger (plus the default primary).
//   ok       -> success
//   rejected -> warning  (Shopee's data refused it; fixable on Shopee)
//   failed   -> danger   (our side broke; retryable)
//   skipped  -> info
//   pending  -> "" (the default grey chip) — a job that ended with pending rows
//               was cancelled or aborted, which is neither good nor bad news.
export const BULK_ITEM_STATUS_TAG_TYPES = {
  ok: "success",
  rejected: "warning",
  failed: "danger",
  skipped: "info",
  pending: ""
};

export function bulkItemStatusTagType(status) {
  const s = stringOf(status).toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(BULK_ITEM_STATUS_TAG_TYPES, s)) return "info";
  return BULK_ITEM_STATUS_TAG_TYPES[s];
}

// bulkItemStatusI18nKey / bulkJobStatusI18nKey: labels for the tags and the
// progress headline. An unknown value gets its own sentence rather than being
// printed raw.
export function bulkItemStatusI18nKey(status) {
  const s = stringOf(status).toLowerCase();
  if (BULK_ITEM_STATUSES.indexOf(s) === -1) return "shopee_bulk_sync.item_status.unknown";
  return "shopee_bulk_sync.item_status." + s;
}

export function bulkJobStatusI18nKey(status) {
  const s = statusOf(status);
  if (BULK_JOB_STATUSES.indexOf(s) === -1) return "shopee_bulk_sync.job_status.unknown";
  return "shopee_bulk_sync.job_status." + s;
}

// bulkItemProductId: the seller product this item landed on, or 0. Drives the
// link to /app-catalog/products/:id/edit — which must NOT be rendered for 0
// (a dry run, a reject, a skip), because /products/0/edit is a dead route.
export function bulkItemProductId(item) {
  if (!item || typeof item !== "object") return 0;
  const id = Number(item.product_id);
  return isFinite(id) && id > 0 ? Math.floor(id) : 0;
}

// bulkItemsParams builds the query for GET :id/items. `status` "" means "all"
// and is omitted so the BE returns every row; page/page_size are clamped to the
// documented bounds.
export function bulkItemsParams(status, page, pageSize) {
  const size = Number(pageSize);
  const p = Number(page);
  const params = {
    page: isFinite(p) && p > 0 ? Math.floor(p) : 1,
    page_size: isFinite(size) && size > 0 ? Math.min(Math.floor(size), 200) : BULK_ITEMS_PAGE_SIZE
  };
  const s = stringOf(status).toLowerCase();
  if (s !== "" && BULK_ITEM_STATUSES.indexOf(s) !== -1) params.status = s;
  return params;
}

// --------------------------------------------------------------------------
// #286 — export the results as .xlsx
// --------------------------------------------------------------------------
//
//   GET /api/platform/v2/shopee/bulk-sync/:id/export?status=
//     200 application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
//         Content-Disposition: attachment; filename="shopee-sync-<job>-<shop>.xlsx";
//                              filename*=UTF-8''shopee-sync-<job>-<%E0%B8%A3…>.xlsx
//     400 {code:"bad_status"}      a ?status= outside the five item statuses
//     404 {code:"bulk_not_found"}
//     409 {code:"bulk_in_flight"}  still queued / listing / running
//
// THE ERROR BODY IS A BLOB, NOT AN OBJECT. The request sets
// responseType:"blob", so axios hands back a Blob even for the 4xx — which is
// why the modal shows one `export_failed` sentence instead of trying to read
// `code` out of a body it would have to await. Everything worth deciding is in
// the STATUS and in the header, and both are synchronous.

// bulkExportFallbackFilename is what a download is called when the server sent no
// usable Content-Disposition at all (a proxy that strips it, a CORS setup that
// does not expose it). `shopee-sync-<jobId>.xlsx` rather than "download.xlsx":
// the operator must be able to tell two exports apart in a Downloads folder even
// on the degraded path.
export function bulkExportFallbackFilename(jobId) {
  const id = Number(jobId);
  const safe = isFinite(id) && id > 0 ? Math.floor(id) : 0;
  return "shopee-sync-" + safe + ".xlsx";
}

// headerValue reads one header out of whatever axios handed back. Header names
// are case-insensitive on the wire and axios has shipped both spellings across
// versions (and a `get()` accessor in v1), so all three are accepted rather than
// having the filename silently fall back on an axios upgrade.
function headerValue(headers, name) {
  if (!headers) return "";
  const lower = name.toLowerCase();
  if (typeof headers.get === "function") {
    const via = headers.get(name) || headers.get(lower);
    if (via) return stringOf(via);
  }
  if (typeof headers !== "object") return "";
  for (const key of Object.keys(headers)) {
    if (String(key).toLowerCase() === lower) return stringOf(headers[key]);
  }
  return "";
}

// sanitiseFilename strips what a download name must not carry. A server is
// trusted for the TEXT of the name and never for its PATH: a Content-Disposition
// of `filename="../../x.xlsx"` must not become a relative path in the anchor's
// download attribute.
function sanitiseFilename(name) {
  const cleaned = stringOf(name)
    .replace(/[\\/]/g, "")
    .replace(/[\u0000-\u001f\u007f"]/g, "")
    .trim();
  return cleaned;
}

// bulkExportFilename picks the name to save the blob under.
//
//   bulkExportFilename(response.headers, jobId) -> "shopee-sync-42-ร้านทดสอบ.xlsx"
//
// THE RFC 5987 PARAMETER WINS. `filename*=UTF-8''…` is the only one of the two
// that can carry a Thai shop name; the plain `filename=` beside it is the ASCII
// fallback the BE builds from the shop ID for clients that do not understand
// the starred form. Preferring the plain one would throw the shop's name away on
// every download — so this reads `filename*` first, percent-decodes it, and only
// falls back when it is absent or undecodable (a truncated escape makes
// decodeURIComponent throw, and a throw here must not cost the download).
export function bulkExportFilename(headers, jobId) {
  const disposition = headerValue(headers, "content-disposition");

  if (disposition !== "") {
    // RFC 5987: filename*=<charset>'<lang>'<percent-encoded>. The charset and
    // the language are both optional in practice, so the value after the second
    // quote is what matters.
    const starred = /filename\*\s*=\s*([^;]+)/i.exec(disposition);
    if (starred) {
      let value = stringOf(starred[1]);
      const quoted = value.indexOf("''");
      if (quoted !== -1) value = value.slice(quoted + 2);
      value = value.replace(/^["']|["']$/g, "");
      try {
        const decoded = sanitiseFilename(decodeURIComponent(value));
        if (decoded !== "") return decoded;
      } catch (e) {
        // A malformed escape (a truncated "%E0%"). FALL THROUGH to the plain
        // parameter rather than returning the raw, still-encoded bytes: saving
        // a file called "%E0%" is worse than saving the ASCII name the BE put
        // beside it for exactly this case.
      }
    }

    // The plain parameter, quoted or bare. The negative lookahead keeps this
    // from matching the `filename*=` above when the starred one comes first.
    const plain = /filename\s*=\s*(?!\*)("([^"]*)"|[^;]+)/i.exec(disposition);
    if (plain) {
      const value = sanitiseFilename(plain[2] !== undefined ? plain[2] : plain[1]);
      if (value !== "") return value;
    }
  }

  return bulkExportFallbackFilename(jobId);
}

// canExportBulk: the Export button is enabled only for a job that has stopped
// moving.
//
// THE SAME RULE THE BE ENFORCES (409 bulk_in_flight). The button is disabled so
// the operator is never offered something that answers 409; the BE refuses
// anyway, because a file exported out of a running job carries counters that
// were never all true at the same moment. A null job (the first poll is still in
// flight) is false, not true.
export function canExportBulk(job) {
  if (job === null || job === undefined) return false;
  return BULK_FINISHED_STATUSES.indexOf(statusOf(job)) !== -1;
}

// bulkExportParams builds the query. "" means "every row" and is omitted, the
// same way bulkItemsParams drops it — so the Export button next to the tabs can
// hand it `this.itemsTab` directly and the "ทั้งหมด" tab exports everything.
export function bulkExportParams(status) {
  const s = stringOf(status).toLowerCase();
  if (s === "" || s === "all") return {};
  if (BULK_ITEM_STATUSES.indexOf(s) === -1) return {};
  return { status: s };
}

// bulkJobCounters is the ok/rejected/failed/skipped row under the progress bar,
// as [{status, count}] in BULK_ITEM_STATUSES order (minus `pending`, which has
// no counter column on the job row — it is total-processed).
export function bulkJobCounters(job) {
  const j = job && typeof job === "object" ? job : {};
  const num = (v) => {
    const n = Number(v);
    return isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };
  return [
    { status: "ok", count: num(j.ok_count) },
    { status: "rejected", count: num(j.rejected_count) },
    { status: "failed", count: num(j.failed_count) },
    { status: "skipped", count: num(j.skipped_count) }
  ];
}
